<template>
  <div class="flex flex-col items-center justify-start min-h-screen xs:max-w-[calc(100vw-2rem)]">
    <div
      v-if="loading"
      class="flex-center flex-col gap-4 w-full px-4"
    >
      <SkeletonLoader class="w-full aspect-image border rounded-2xl" />
      <SkeletonLoader class="w-2/3 h-8 mt-4" />
      <div class="flex-center flex-col gap-1 my-2 w-full">
        <SkeletonLoader class="w-3/4 h-4" />
        <SkeletonLoader class="w-2/5 h-4" />
      </div>
      <SkeletonLoader class="w-28 h-4" />
      <SkeletonLoader class="w-40 h-10 rounded-full" />
    </div>
    <ProductInfoModal v-else-if="product" v-bind="{ product }"/>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { client } from '@/graphql'
import { gidToId, graphqlUnpack, setHash } from '@/utils'
import { useLoading } from '@/composables'
import { imageRef } from '@/graphql/utils/products'
import { GetBundleProduct, type GetBundleProductQuery } from '@/graphql/queries/product'
import SkeletonLoader from '../shared/v2/skeleton-loader.vue'
import ProductInfoModal from './product-info-modal.vue'
import type { Image } from '@/types/shopify'
import type { BundleProductVariant, GraphqlBundleProduct } from '@/types'

const props = defineProps<{
  bundleProductId: number
}>()

const product = ref<GraphqlBundleProduct>()

onMounted(async () => {
  const data = await loadProduct(props.bundleProductId)
  // wait until animation ends to show product
  setTimeout(async () => {
    await nextTick()
    product.value = data
  }, 200)
})

onBeforeUnmount(() => setHash(''))

const [ loading, loadProduct ] = useLoading(async (id: number) => {
  const { data: { product } } = await client.query<GetBundleProductQuery>({
    query: GetBundleProduct,
    variables: {
      id: `gid://shopify/Product/${id}`
    }
  })
  const variants = graphqlUnpack(product.variants.edges) as BundleProductVariant[]
  const unpackedProduct = {
    ...product,
    sku: variants[0].sku,
    nid: gidToId(product.id),
    variants,
    images: graphqlUnpack<Image>(product.images.edges),
    flavorForwardImage: imageRef(product.flavorForwardImage),
    nutritionImage: imageRef(product.nutritionImage),
    nutritionFlavorForwardImage: imageRef(product.nutritionFlavorForwardImage),
    zoomImage: imageRef(product.zoomImage),
    ingredients: JSON.parse(product.ingredients?.value ?? '[]')
  } as GraphqlBundleProduct

  return unpackedProduct
})
</script>

<style>
.product-modal-wrapper .modal {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
</style>
